import { useAtom, useAtomValue, useSetAtom } from "jotai";
import type { FC } from "react";
import { useContext, useEffect, useRef } from "react";
import { currentTimeAtom } from "$/modules/audio/states/index.ts";
import {
	type ProcessedLyricLine,
	processedLyricLinesAtom,
} from "$/modules/segmentation/utils/segment-processing.ts";
import {
	previewLineAtom,
	timelineDragAtom,
	selectionPanOffsetAtom,
} from "$/modules/spectrogram/states/dnd.ts";
import {
	spectrogramAggressiveCullingAtom,
	spectrogramCullBufferPxAtom,
} from "$/modules/spectrogram/states/index.ts";
import { syncTargetModeAtom } from "$/modules/settings/states/sync.ts";
import {
	commitUpdatedLine,
	getUpdatedLineForDivider,
	getUpdatedLineForWordPan,
} from "$/modules/spectrogram/utils/timeline-mutations.ts";
import { isSnappingEnabledAtom, lyricLinesAtom, selectedLinesAtom, selectedWordsAtom, showUnselectedLinesAtom } from "$/states/main.ts";
import { globalStore } from "$/states/store.ts";
import { LyricLineSegment } from "./LyricLineSegment";
import styles from "./LyricTimelineOverlay.module.css";
import { SpectrogramContext } from "./SpectrogramContext.ts";

interface LyricTimelineOverlayProps {
	clientWidth: number;
	hiddenLineIds?: Set<string> | null;
	splitLayersEnabled: boolean;
}

const SNAP_THRESHOLD_PX = 7;

export const LyricTimelineOverlay: FC<LyricTimelineOverlayProps> = ({
	clientWidth,
	hiddenLineIds,
	splitLayersEnabled,
}) => {
	const processedLines = useAtomValue(processedLyricLinesAtom);
	const [timelineDrag, setTimelineDrag] = useAtom(timelineDragAtom);
	const setSelectionPanOffset = useSetAtom(selectionPanOffsetAtom);
	const setPreviewLine = useSetAtom(previewLineAtom);
	const currentTime = useAtomValue(currentTimeAtom);
	const snapTargetsMs = useRef<number[]>([]);
	const { scrollContainerRef, zoom, scrollLeft } =
		useContext(SpectrogramContext);

	const showUnselectedLines = useAtomValue(showUnselectedLinesAtom);
	const selectedLines = useAtomValue(selectedLinesAtom);
	const isSnappingEnabled = useAtomValue(isSnappingEnabledAtom);

	const isAggressiveCulling = useAtomValue(spectrogramAggressiveCullingAtom);
	const cullBufferPx = useAtomValue(spectrogramCullBufferPxAtom);

	useEffect(() => {
		if (!timelineDrag) {
			return;
		}

		const handleGlobalMouseMove = (event: MouseEvent) => {
			event.preventDefault();

			switch (timelineDrag.type) {
				case "divider": {
					const { startX, lineId, segmentIndex, isGapCreation, zoom } =
						timelineDrag;
					const lineBeingDragged = processedLines.find((l) => l.id === lineId);
					if (!lineBeingDragged) return;

					const deltaX = event.clientX - startX;
					const deltaTimeMs = Math.round((deltaX / zoom) * 1000);
					let newTime =
						(segmentIndex === -1
							? lineBeingDragged.startTime
							: lineBeingDragged.segments[segmentIndex].endTime) + deltaTimeMs;

					if (!event.shiftKey) {
						let closestSnapTime: number | null = null;
						let minDistancePx = SNAP_THRESHOLD_PX;
						const newTimePx = (newTime / 1000) * zoom;

						for (const targetTime of snapTargetsMs.current) {
							const targetTimePx = (targetTime / 1000) * zoom;
							const distancePx = Math.abs(newTimePx - targetTimePx);
							if (distancePx < minDistancePx) {
								minDistancePx = distancePx;
								closestSnapTime = targetTime;
							}
						}
						if (closestSnapTime !== null) newTime = closestSnapTime;
					}

					const preview = getUpdatedLineForDivider(
						lineBeingDragged,
						segmentIndex,
						newTime,
						isGapCreation,
						zoom,
					);
					setPreviewLine(preview);
					break;
				}

				case "word-pan": {
					const { lineId, wordId, startX, initialWordStartMS } =
						timelineDrag;
					const processedLine = processedLines.find((l) => l.id === lineId);
					if (!processedLine) return;

					let deltaX = event.clientX - startX;

					if (isSnappingEnabled && !event.shiftKey) {
						// Calculate current dragged boundaries to see if they snap
						const wordToDrag = processedLine.segments.find((w) => w.type === "word" && w.id === wordId);
						if (wordToDrag) {
							const wordDuration = wordToDrag.endTime - wordToDrag.startTime;
							const newStartMs = initialWordStartMS + (deltaX / zoom) * 1000;
							const newEndMs = newStartMs + wordDuration;

							const newStartPx = (newStartMs / 1000) * zoom;
							const newEndPx = (newEndMs / 1000) * zoom;

							let closestSnapOffsetPx: number | null = null;
							let minDistancePx = SNAP_THRESHOLD_PX;

							for (const targetTime of snapTargetsMs.current) {
								const targetTimePx = (targetTime / 1000) * zoom;

								// Check start boundary snap
								const distStart = Math.abs(newStartPx - targetTimePx);
								if (distStart < minDistancePx) {
									minDistancePx = distStart;
									closestSnapOffsetPx = targetTimePx - newStartPx; // How much to adjust deltaX
								}

								// Check end boundary snap
								const distEnd = Math.abs(newEndPx - targetTimePx);
								if (distEnd < minDistancePx) {
									minDistancePx = distEnd;
									closestSnapOffsetPx = targetTimePx - newEndPx; // How much to adjust deltaX
								}
							}

							if (closestSnapOffsetPx !== null) {
								deltaX += closestSnapOffsetPx;
							}
						}
					}

					const deltaTimeMs = (deltaX / zoom) * 1000;
					const desiredNewStartMS = initialWordStartMS + deltaTimeMs;

					const preview = getUpdatedLineForWordPan(
						processedLine,
						wordId,
						desiredNewStartMS,
						zoom,
					);
					setPreviewLine(preview);
					break;
				}

				case "selection-pan": {
					const { startX } = timelineDrag;
					let deltaX = event.clientX - startX;

					if (isSnappingEnabled && !event.shiftKey) {
						// We need to find the boundaries of the selected items at their initial positions.
						// The boundaries are stored in `selectionPanInitialBounds` or we can just iterate.
						// Rather than pre-calculating every edge, we can map the current selected atom state.
						const selectedLineIds = globalStore.get(selectedLinesAtom);
						const selectedWordIds = globalStore.get(selectedWordsAtom);

						let bestSnapOffsetPx: number | null = null;
						let minDistancePx = SNAP_THRESHOLD_PX;

						for (const line of processedLines) {
							if (!selectedLineIds.has(line.id)) continue;

							const hasSelectedWords = line.segments.some((s) => s.type === "word" && selectedWordIds.has(s.id));
							const segmentsToMove = hasSelectedWords
								? line.segments.filter((s) => s.type === "word" && selectedWordIds.has(s.id))
								: line.segments.filter((s) => s.type === "word"); // Whole line moves

							for (const s of segmentsToMove) {
								// Predict where this segment WILL be
								const newStartMs = s.startTime + (deltaX / zoom) * 1000;
								const newEndMs = s.endTime + (deltaX / zoom) * 1000;

								const newStartPx = (newStartMs / 1000) * zoom;
								const newEndPx = (newEndMs / 1000) * zoom;

								for (const targetTime of snapTargetsMs.current) {
									const targetTimePx = (targetTime / 1000) * zoom;

									const distStart = Math.abs(newStartPx - targetTimePx);
									if (distStart < minDistancePx) {
										minDistancePx = distStart;
										bestSnapOffsetPx = targetTimePx - newStartPx;
									}

									const distEnd = Math.abs(newEndPx - targetTimePx);
									if (distEnd < minDistancePx) {
										minDistancePx = distEnd;
										bestSnapOffsetPx = targetTimePx - newEndPx;
									}
								}
							}
						}

						if (bestSnapOffsetPx !== null) {
							deltaX += bestSnapOffsetPx;
						}
					}

					const timeDeltaMS = (deltaX / zoom) * 1000;

					setSelectionPanOffset(timeDeltaMS);
					break;
				}
			}
		};

		const handleGlobalMouseUp = (event: MouseEvent) => {
			event.preventDefault();

			if (timelineDrag.type === "selection-pan") {
				// We need to commit the current pan offset to all selected lines/words
				const offsetMs = globalStore.get(selectionPanOffsetAtom) as number;
				if (offsetMs !== 0) {
					// Apply offset to all selected words, or entire lines if no words are selected in that line
					globalStore.set(lyricLinesAtom, (prev) => {
						const selectedLineIds = globalStore.get(selectedLinesAtom);
						const selectedWordIds = globalStore.get(selectedWordsAtom);
						const syncTargetMode = globalStore.get(syncTargetModeAtom);
						const newLines = prev.lyricLines.map((line: typeof prev.lyricLines[0]) => {
							if (!selectedLineIds.has(line.id)) return line;

							const hasSelectedWords = line.words.some((w: typeof line.words[0]) => selectedWordIds.has(w.id));
							const newWords = line.words.map((w: typeof line.words[0]) => {
								// Move word if it's explicitly selected, OR if the line is selected but none of its words are explicitly selected (meaning whole line is selected)
								if (selectedWordIds.has(w.id) || (selectedLineIds.has(line.id) && !hasSelectedWords)) {
									if (syncTargetMode === "line" && w.startTime === 0 && w.endTime === 0) {
										return w;
									}
									return {
										...w,
										startTime: Math.max(0, w.startTime + offsetMs),
										endTime: Math.max(0, w.endTime + offsetMs),
									};
								}
								return w;
							});

							// Recompute line start/end bounds correctly
							const newLine = { ...line, words: newWords };
							if (newLine.words.length > 0) {
								newLine.startTime = newLine.words[0].startTime;
								newLine.endTime = newLine.words[newLine.words.length - 1].endTime;
							} else if (selectedLineIds.has(line.id)) {
								// Move the empty line itself
								newLine.startTime = Math.max(0, newLine.startTime + offsetMs);
								newLine.endTime = Math.max(0, newLine.endTime + offsetMs);
							}
							return newLine;
						});
						return { ...prev, lyricLines: newLines };
					});
				}
				setSelectionPanOffset(0);
			} else {
				const lastSnappedLine = globalStore.get(previewLineAtom);
				if (lastSnappedLine) {
					commitUpdatedLine(lastSnappedLine);
				}
			}

			setTimelineDrag(null);
			setPreviewLine(null);
		};

		let needsBoundarySnapping = false;

		if (timelineDrag.type === "divider") {
			const { segmentIndex } = timelineDrag;
			const lineBeingDragged = processedLines.find(
				(l) => l.id === timelineDrag.lineId,
			);
			if (lineBeingDragged) {
				needsBoundarySnapping =
					segmentIndex === -1 ||
					segmentIndex === lineBeingDragged.segments.length - 1;
			}
		}

		if (needsBoundarySnapping) {
			const lineId = "lineId" in timelineDrag ? timelineDrag.lineId : "";
			const targets: number[] = [currentTime];
			const otherLineBoundaries = processedLines
				.filter((line) => line.id !== lineId)
				.flatMap((line) => [line.startTime, line.endTime]);
			targets.push(...otherLineBoundaries);
			snapTargetsMs.current = targets.filter(
				(time): time is number => time != null,
			);
		} else if (isSnappingEnabled && (timelineDrag.type === "word-pan" || timelineDrag.type === "selection-pan")) {
			const targets: number[] = [currentTime];
			const selectedLineIds = globalStore.get(selectedLinesAtom);
			const selectedWordIds = globalStore.get(selectedWordsAtom);

			for (const line of processedLines) {
				const isLineSelected = selectedLineIds.has(line.id);
				const hasSelectedWords = line.segments.some((s) => s.type === "word" && selectedWordIds.has(s.id));

				// If the whole line is moving, don't add any of its words as targets
				if (isLineSelected && !hasSelectedWords) {
					continue;
				}

				for (const s of line.segments) {
					if (s.type === "word") {
						// Don't add explicitly grabbed words as targets to snap to
						if (selectedWordIds.has(s.id)) continue;

						// Additionally, during a word pan, don't snap to the exact word we are panning if it wasn't selected (word-pan auto-selects it but just in case)
						if (timelineDrag.type === "word-pan" && timelineDrag.wordId === s.id) continue;

						targets.push(s.startTime, s.endTime);
					}
				}

				// Also add line boundaries as targets
				if (!isLineSelected) {
					targets.push(line.startTime, line.endTime);
				}
			}

			snapTargetsMs.current = targets.filter(
				(time): time is number => time != null,
			);
		}

		window.addEventListener("mousemove", handleGlobalMouseMove);
		window.addEventListener("mouseup", handleGlobalMouseUp, { once: true });

		return () => {
			window.removeEventListener("mousemove", handleGlobalMouseMove);
			window.removeEventListener("mouseup", handleGlobalMouseUp);
			snapTargetsMs.current = [];
			setPreviewLine(null);
		};
	}, [
		timelineDrag,
		setTimelineDrag,
		setPreviewLine,
		zoom,
		scrollLeft,
		scrollContainerRef,
		processedLines,
		currentTime,
	]);

	const bufferPx = isAggressiveCulling ? cullBufferPx : 500;
	const viewStartMs = ((scrollLeft - bufferPx) / zoom) * 1000;
	const viewEndMs = ((scrollLeft + clientWidth + bufferPx) / zoom) * 1000;

	const linesToRenderSet = new Set<ProcessedLyricLine>();
	let closestLeft: ProcessedLyricLine | null = null;
	let foundClosestRight = false;

	// Binary search for the first line that MIGHT be visible
	let startIndex = 0;
	let low = 0;
	let high = processedLines.length - 1;

	while (low <= high) {
		const mid = Math.floor((low + high) / 2);
		const line = processedLines[mid];
		if (line.endTime != null && line.endTime >= viewStartMs) {
			startIndex = mid;
			high = mid - 1; // Look for an earlier one
		} else {
			low = mid + 1;
		}
	}

	// Start grabbing lines from the found start index
	for (let i = startIndex; i < processedLines.length; i++) {
		const line = processedLines[i];
		if (line.startTime == null || line.endTime == null) continue;

		const inBufferedView =
			line.endTime >= viewStartMs && line.startTime <= viewEndMs;

		if (inBufferedView) {
			linesToRenderSet.add(line);
			continue;
		}

		if (line.endTime < viewStartMs) {
			closestLeft = line;
		}

		// Exit early as soon as we overshoot the right bounding box
		if (line.startTime > viewEndMs) {
			if (!foundClosestRight) {
				linesToRenderSet.add(line);
				foundClosestRight = true;
			}
			break;
		}
	}

	if (closestLeft) {
		linesToRenderSet.add(closestLeft);
	}

	const visibleLines = Array.from(linesToRenderSet);

	let linesToRender: ProcessedLyricLine[] = visibleLines;

	if (hiddenLineIds && hiddenLineIds.size > 0) {
		linesToRender = linesToRender.filter((line) => !hiddenLineIds.has(line.id));
	}

	if (!showUnselectedLines) {
		linesToRender = linesToRender.filter((line) => selectedLines.has(line.id));
	}

	return (
		<div className={`${styles.overlay} ${splitLayersEnabled ? styles.splitEnabled : ''}`}>
			{splitLayersEnabled && <div className={styles.splitDivider} />}
			{linesToRender.map((line) => (
				<LyricLineSegment
					key={line.id}
					line={line}
					allLines={processedLines}
					splitLayersEnabled={splitLayersEnabled}
					zoom={zoom}
				/>
			))}
		</div>
	);
};
